import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  Award, BadgeCheck, Plus, Eye, Download, Trash2, Search, ChevronDown,
  ChevronRight as ChevronRightIcon, FileText, Building2, Calendar,
  CheckCircle2, AlertCircle, MinusCircle, Circle, ArrowLeft, Sparkles, Edit,
  Upload, ClipboardCheck, Bell, UserCheck, ExternalLink, Info, Paperclip,
  Image as ImageIcon, FileType, Loader2, ListChecks, AlertTriangle
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATI = [
  { key: 'conforme', label: 'Conforme', color: 'bg-emerald-100 text-emerald-700 border-emerald-300', icon: CheckCircle2, dot: 'bg-emerald-500' },
  { key: 'parziale', label: 'Parziale', color: 'bg-amber-100 text-amber-700 border-amber-300', icon: AlertCircle, dot: 'bg-amber-500' },
  { key: 'non_conforme', label: 'Non Conforme', color: 'bg-rose-100 text-rose-700 border-rose-300', icon: MinusCircle, dot: 'bg-rose-500' },
  { key: 'non_applicabile', label: 'N.A.', color: 'bg-slate-100 text-slate-700 border-slate-300', icon: Circle, dot: 'bg-slate-400' },
];

const CATEGORIE = {
  strutturali: { label: 'Strutturali', color: 'bg-cyan-50 text-cyan-700' },
  tecnologici: { label: 'Tecnologici', color: 'bg-blue-50 text-blue-700' },
  organizzativi: { label: 'Organizzativi', color: 'bg-indigo-50 text-indigo-700' },
  qualita: { label: 'Qualità', color: 'bg-amber-50 text-amber-700' },
};

const findStato = (k) => STATI.find(s => s.key === k);

export default function Accreditamenti({ user }) {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [requirements, setRequirements] = useState([]);
  const [stats, setStats] = useState(null);
  const [procedures, setProcedures] = useState([]);
  const [users, setUsers] = useState([]);
  const [myAssignments, setMyAssignments] = useState([]);

  const [showCreateProject, setShowCreateProject] = useState(false);
  const [projectForm, setProjectForm] = useState({ codice: '', nome: '', descrizione: '', ente: '', anno_riferimento: new Date().getFullYear(), riferimento_normativo: '' });

  const [showRequirementDialog, setShowRequirementDialog] = useState(false);
  const [editingReq, setEditingReq] = useState(null);
  const [reqForm, setReqForm] = useState({ stato: '', note: '', completato: false, procedure_collegate_ids: [], evidenze_text: '', responsabile_user_id: '', scadenza_verifica: '' });

  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importResult, setImportResult] = useState(null);

  const [showMyAssignments, setShowMyAssignments] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);

  const [filterStato, setFilterStato] = useState('all');
  const [filterCategoria, setFilterCategoria] = useState('all');
  const [search, setSearch] = useState('');
  const [expandedSections, setExpandedSections] = useState({});

  const isSuperAdmin = user.role === 'super_amministratore';
  const isAdmin = isSuperAdmin || user.role === 'amministratore';

  const authHeaders = useMemo(() => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  }, []);

  const fetchProjects = useCallback(async () => {
    try {
      const [prj, mine] = await Promise.all([
        axios.get(`${API}/accreditamenti/projects`, { headers: authHeaders }),
        axios.get(`${API}/accreditamenti/my-assignments`, { headers: authHeaders }).catch(() => ({ data: [] })),
      ]);
      setProjects(prj.data || []);
      setMyAssignments(mine.data || []);
    } catch (error) {
      console.error('Errore caricamento progetti:', error);
      if (error.response?.status === 403) {
        toast.error('Accesso al modulo Accreditamenti non autorizzato');
      }
    }
  }, [authHeaders]);

  const fetchProjectDetails = useCallback(async (projectId) => {
    try {
      const [reqs, st, proc, usr] = await Promise.all([
        axios.get(`${API}/accreditamenti/projects/${projectId}/requirements`, { headers: authHeaders }),
        axios.get(`${API}/accreditamenti/projects/${projectId}/statistics`, { headers: authHeaders }),
        axios.get(`${API}/procedure`, { headers: authHeaders }).catch(() => ({ data: [] })),
        isAdmin ? axios.get(`${API}/users`, { headers: authHeaders }).catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
      ]);
      setRequirements(reqs.data || []);
      setStats(st.data);
      setProcedures(proc.data || []);
      setUsers(usr.data || []);
    } catch (error) {
      toast.error('Errore caricamento dettagli progetto');
    }
  }, [authHeaders, isAdmin]);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);
  useEffect(() => {
    if (selectedProject) fetchProjectDetails(selectedProject.id);
  }, [selectedProject, fetchProjectDetails]);

  const seedAbruzzo = async () => {
    const exists = projects.some(p => p.codice === 'ADI_ABRUZZO_DGR272');
    const msg = exists
      ? 'Aggiornare il catalogo requisiti DGR 272 Regione Abruzzo all\'ultima versione del manuale?\n\nVerranno aggiornate descrizioni e checklist per tutti i 37 requisiti.\n\n✓ Le risposte gia inserite (stato, note, allegati, assegnazioni) NON verranno toccate.'
      : 'Caricare il catalogo requisiti DGR 272 Regione Abruzzo (37 requisiti con checklist puntuali)?';
    if (!window.confirm(msg)) return;
    try {
      const res = await axios.post(`${API}/accreditamenti/seed-abruzzo`, {}, { headers: authHeaders });
      const d = res.data || {};
      toast.success(`Manuale Abruzzo aggiornato: ${d.requisiti_count || 0} requisiti (nuovi: ${d.inserted || 0}, aggiornati: ${d.updated || 0})`);
      await fetchProjects();
      if (selectedProject?.codice === 'ADI_ABRUZZO_DGR272') {
        await fetchProjectDetails(selectedProject.id);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore seed');
    }
  };

  const createProject = async () => {
    if (!projectForm.codice || !projectForm.nome) { toast.error('Codice e Nome obbligatori'); return; }
    try {
      await axios.post(`${API}/accreditamenti/projects`, projectForm, { headers: authHeaders });
      toast.success('Progetto creato');
      setShowCreateProject(false);
      setProjectForm({ codice: '', nome: '', descrizione: '', ente: '', anno_riferimento: new Date().getFullYear(), riferimento_normativo: '' });
      fetchProjects();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore creazione');
    }
  };

  const deleteProject = async (p) => {
    if (!window.confirm(`Eliminare il progetto "${p.nome}" e tutti i suoi requisiti/risposte?`)) return;
    try {
      await axios.delete(`${API}/accreditamenti/projects/${p.id}`, { headers: authHeaders });
      toast.success('Progetto eliminato');
      if (selectedProject?.id === p.id) setSelectedProject(null);
      fetchProjects();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const downloadPdf = async (p) => {
    try {
      const res = await axios.get(`${API}/accreditamenti/projects/${p.id}/pdf`, { headers: authHeaders, responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `autocert_${p.codice}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Errore download PDF');
    }
  };

  const downloadGapExcel = async (p) => {
    try {
      const res = await axios.get(`${API}/accreditamenti/projects/${p.id}/gap-analysis-excel`, { headers: authHeaders, responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `gap_analysis_${p.codice}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('Gap analysis scaricata');
    } catch (error) {
      toast.error('Errore download Excel');
    }
  };

  const openRequirement = async (req) => {
    setEditingReq(req);
    const r = req.response || {};
    setReqForm({
      stato: r.stato || '',
      note: r.note || '',
      completato: !!r.completato,
      procedure_collegate_ids: r.procedure_collegate_ids || [],
      evidenze_text: r.evidenze_text || '',
      responsabile_user_id: r.responsabile_user_id || '',
      scadenza_verifica: r.scadenza_verifica ? r.scadenza_verifica.substring(0, 10) : '',
      checklist_state: r.checklist_state || {},
      checklist_custom_items: r.checklist_custom_items || [],
    });
    setAttachments([]);
    setShowRequirementDialog(true);
    // Fetch attachments
    try {
      const res = await axios.get(
        `${API}/accreditamenti/projects/${selectedProject.id}/responses/${req.id}/attachments`,
        { headers: authHeaders },
      );
      setAttachments(res.data || []);
    } catch (error) {
      // Silent
    }
  };

  const uploadAttachment = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !editingReq || !selectedProject) return;
    if (file.size > 25 * 1024 * 1024) {
      toast.error('File troppo grande (max 25 MB)');
      return;
    }
    setUploadingAttachment(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(
        `${API}/accreditamenti/projects/${selectedProject.id}/responses/${editingReq.id}/attachments`,
        formData,
        { headers: { ...authHeaders, 'Content-Type': 'multipart/form-data' } },
      );
      const att = res.data.attachment;
      const savedKb = ((att.original_size - att.compressed_size) / 1024).toFixed(0);
      toast.success(`Allegato caricato · compresso del ${att.saved_pct}% (-${savedKb} KB)`);
      setAttachments(prev => [att, ...prev]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore caricamento');
    } finally {
      setUploadingAttachment(false);
      e.target.value = '';
    }
  };

  const downloadAttachment = async (att) => {
    try {
      const res = await axios.get(`${API}/accreditamenti/attachments/${att.id}/download`, {
        headers: authHeaders,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = att.original_filename;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Errore download allegato');
    }
  };

  const deleteAttachment = async (att) => {
    if (!window.confirm(`Eliminare l'allegato "${att.original_filename}"?`)) return;
    try {
      await axios.delete(`${API}/accreditamenti/attachments/${att.id}`, { headers: authHeaders });
      toast.success('Allegato eliminato');
      setAttachments(prev => prev.filter(a => a.id !== att.id));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore eliminazione');
    }
  };

  const formatBytes = (b) => {
    if (!b) return '0 B';
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / (1024 * 1024)).toFixed(2)} MB`;
  };

  const saveRequirement = async () => {
    if (!editingReq) return;
    try {
      // Convert empty strings to null for clean upsert
      const payload = { ...reqForm };
      if (!payload.responsabile_user_id) payload.responsabile_user_id = '';
      if (!payload.scadenza_verifica) payload.scadenza_verifica = null;
      await axios.put(
        `${API}/accreditamenti/projects/${selectedProject.id}/responses/${editingReq.id}`,
        payload,
        { headers: authHeaders },
      );
      toast.success('Risposta salvata');
      setShowRequirementDialog(false);
      fetchProjectDetails(selectedProject.id);
      fetchProjects(); // refresh assignments
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore salvataggio');
    }
  };

  const createAuditFromRequirement = async () => {
    if (!editingReq || !selectedProject) return;
    if (!['non_conforme', 'parziale'].includes(reqForm.stato)) {
      toast.error('Salva prima il requisito come Non Conforme o Parziale');
      return;
    }
    if (!window.confirm(`Generare un audit interno per il requisito ${editingReq.codice}? Le modifiche correnti verranno salvate prima di creare l'audit.`)) return;
    try {
      // Auto-save current form state to prevent inconsistency between UI and DB
      const payload = { ...reqForm };
      if (!payload.responsabile_user_id) payload.responsabile_user_id = '';
      if (!payload.scadenza_verifica) payload.scadenza_verifica = null;
      await axios.put(
        `${API}/accreditamenti/projects/${selectedProject.id}/responses/${editingReq.id}`,
        payload,
        { headers: authHeaders },
      );
      const res = await axios.post(
        `${API}/accreditamenti/projects/${selectedProject.id}/responses/${editingReq.id}/create-audit`,
        {},
        { headers: authHeaders },
      );
      if (res.data.already_exists) {
        toast.info(`Audit già esistente: ${res.data.numero}`);
      } else {
        toast.success(`Audit creato: ${res.data.numero} (${res.data.checklist_count} domande, 1 finding)`);
      }
      setShowRequirementDialog(false);
      fetchProjectDetails(selectedProject.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore creazione audit');
    }
  };

  const importRequirementsFile = async () => {
    if (!importFile || !selectedProject) return;
    const formData = new FormData();
    formData.append('file', importFile);
    try {
      const res = await axios.post(
        `${API}/accreditamenti/projects/${selectedProject.id}/import-requirements`,
        formData,
        { headers: { ...authHeaders, 'Content-Type': 'multipart/form-data' } },
      );
      setImportResult(res.data);
      toast.success(`Import completato: ${res.data.inserted} nuovi, ${res.data.updated} aggiornati`);
      fetchProjectDetails(selectedProject.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore import');
    }
  };

  const downloadImportTemplate = async () => {
    try {
      const res = await axios.get(`${API}/accreditamenti/import-template`, { headers: authHeaders, responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `template_import_requisiti.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Errore download template');
    }
  };

  const sendReminders = async () => {
    if (!window.confirm('Inviare email di reminder a tutti i responsabili con verifiche in scadenza (T-7gg) o scadute?')) return;
    try {
      const res = await axios.post(`${API}/accreditamenti/send-reminders`, {}, { headers: authHeaders });
      toast.success(`Reminder inviati: ${res.data.sent} email · ${res.data.users_notified} utenti notificati`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore invio reminder');
    }
  };

  // Filter + group requirements
  const grouped = useMemo(() => {
    const filtered = requirements.filter(r => {
      const stato = r.response?.stato;
      if (filterStato === 'da_valutare' && stato) return false;
      if (filterStato !== 'all' && filterStato !== 'da_valutare' && stato !== filterStato) return false;
      if (filterCategoria !== 'all' && r.categoria !== filterCategoria) return false;
      if (search) {
        const s = search.toLowerCase();
        const t = `${r.codice} ${r.titolo} ${r.descrizione}`.toLowerCase();
        if (!t.includes(s)) return false;
      }
      return true;
    });
    const groups = {};
    filtered.forEach(r => {
      const cap = r.capitolo || '—';
      const sez = r.sezione || '—';
      if (!groups[cap]) groups[cap] = {};
      if (!groups[cap][sez]) groups[cap][sez] = [];
      groups[cap][sez].push(r);
    });
    return groups;
  }, [requirements, filterStato, filterCategoria, search]);

  const toggleSection = (key) => setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));

  // Project list view
  if (!selectedProject) {
    const overdueCount = myAssignments.filter(a => a.is_overdue).length;
    const upcomingCount = myAssignments.filter(a => !a.is_overdue && a.days_left !== null && a.days_left <= 7).length;
    return (
      <div className="space-y-4" data-testid="accreditamenti-tab-content">
        {/* My assignments banner */}
        {myAssignments.length > 0 && (
          <div className={`rounded-lg border-2 p-4 ${overdueCount > 0 ? 'border-rose-300 bg-rose-50' : 'border-cyan-300 bg-cyan-50'}`} data-testid="my-assignments-banner">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3">
                <Bell className={`h-6 w-6 mt-0.5 ${overdueCount > 0 ? 'text-rose-600' : 'text-cyan-600'}`} />
                <div>
                  <p className={`font-semibold ${overdueCount > 0 ? 'text-rose-900' : 'text-cyan-900'}`}>
                    Hai {myAssignments.length} verifich{myAssignments.length === 1 ? 'a' : 'e'} di accreditamento assegnat{myAssignments.length === 1 ? 'a' : 'e'}
                  </p>
                  <p className="text-sm text-gray-700 mt-0.5">
                    {overdueCount > 0 && <span className="text-rose-700 font-semibold">{overdueCount} scadut{overdueCount === 1 ? 'a' : 'e'}</span>}
                    {overdueCount > 0 && upcomingCount > 0 && ' · '}
                    {upcomingCount > 0 && <span className="text-amber-700 font-semibold">{upcomingCount} in scadenza</span>}
                    {overdueCount === 0 && upcomingCount === 0 && <span>Tutto sotto controllo</span>}
                  </p>
                </div>
              </div>
              <Button size="sm" variant="outline" onClick={() => setShowMyAssignments(true)} data-testid="view-my-assignments"><Eye className="h-4 w-4 mr-1" />Visualizza</Button>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between bg-white rounded-lg border border-gray-200 p-4 flex-wrap gap-3">
          <div>
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2"><Award className="h-6 w-6 text-cyan-600" />Accreditamenti</h2>
            <p className="text-sm text-gray-600">Gestisci progetti di accreditamento e autocertificazioni</p>
          </div>
          {isSuperAdmin && (
            <div className="flex gap-2 flex-wrap">
              <Button variant="outline" onClick={sendReminders} data-testid="send-reminders-btn"><Bell className="h-4 w-4 mr-1" />Invia reminder</Button>
              <Button variant="outline" onClick={seedAbruzzo} data-testid="seed-abruzzo-btn" title="Aggiorna descrizioni e checklist dei 37 requisiti DGR 272 dall'ultima versione del manuale. Le risposte gia inserite vengono preservate.">
                <Sparkles className="h-4 w-4 mr-1" />
                {projects.some(p => p.codice === 'ADI_ABRUZZO_DGR272') ? 'Aggiorna manuale Abruzzo' : 'Carica DGR 272 Abruzzo'}
              </Button>
              <Button onClick={() => setShowCreateProject(true)} className="bg-cyan-600 hover:bg-cyan-700" data-testid="create-project-btn"><Plus className="h-4 w-4 mr-1" />Nuovo progetto</Button>
            </div>
          )}
        </div>

        {projects.length === 0 ? (
          <div className="bg-white rounded-lg border border-dashed border-gray-300 p-12 text-center">
            <Award className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 mb-4">Nessun progetto di accreditamento disponibile</p>
            {isSuperAdmin && (
              <Button onClick={seedAbruzzo} className="bg-cyan-600 hover:bg-cyan-700"><Sparkles className="h-4 w-4 mr-1" />Inizia con DGR 272 Regione Abruzzo</Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {projects.map(p => (
              <ProjectCard key={p.id} project={p} onOpen={() => setSelectedProject(p)} onDownload={() => downloadPdf(p)} onDelete={() => deleteProject(p)} canDelete={isSuperAdmin} authHeaders={authHeaders} />
            ))}
          </div>
        )}

        {/* Create project dialog */}
        <Dialog open={showCreateProject} onOpenChange={setShowCreateProject}>
          <DialogContent data-testid="create-project-dialog">
            <DialogHeader><DialogTitle>Nuovo progetto di accreditamento</DialogTitle><DialogDescription>Definisci un nuovo progetto custom (es. ISO 9001, JCI). Per Regione Abruzzo usa il pulsante "Carica DGR 272".</DialogDescription></DialogHeader>
            <div className="space-y-3">
              <div><Label>Codice *</Label><Input value={projectForm.codice} onChange={e => setProjectForm(f => ({ ...f, codice: e.target.value.toUpperCase() }))} placeholder="ES. ISO_9001_2024" data-testid="project-codice" /></div>
              <div><Label>Nome *</Label><Input value={projectForm.nome} onChange={e => setProjectForm(f => ({ ...f, nome: e.target.value }))} placeholder="Es. ISO 9001:2015 Sistema Qualità" data-testid="project-nome" /></div>
              <div><Label>Ente</Label><Input value={projectForm.ente} onChange={e => setProjectForm(f => ({ ...f, ente: e.target.value }))} placeholder="Es. CSQ, DNV, Regione X..." /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Anno</Label><Input type="number" value={projectForm.anno_riferimento || ''} onChange={e => setProjectForm(f => ({ ...f, anno_riferimento: parseInt(e.target.value) || null }))} /></div>
                <div><Label>Riferimento normativo</Label><Input value={projectForm.riferimento_normativo} onChange={e => setProjectForm(f => ({ ...f, riferimento_normativo: e.target.value }))} placeholder="Es. ISO 9001:2015" /></div>
              </div>
              <div><Label>Descrizione</Label><Textarea rows={3} value={projectForm.descrizione} onChange={e => setProjectForm(f => ({ ...f, descrizione: e.target.value }))} /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateProject(false)}>Annulla</Button>
              <Button onClick={createProject} className="bg-cyan-600 hover:bg-cyan-700" data-testid="project-save">Crea</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Project detail view
  return (
    <div className="space-y-4" data-testid="accreditamenti-project-detail">
      {/* Project header */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <Button variant="ghost" size="sm" onClick={() => setSelectedProject(null)} className="mb-2 -ml-2" data-testid="back-to-projects"><ArrowLeft className="h-4 w-4 mr-1" />Torna ai progetti</Button>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className="px-2 py-0.5 rounded-full bg-cyan-100 text-cyan-700 text-xs font-mono font-bold">{selectedProject.codice}</span>
              {selectedProject.ente && <span className="text-xs text-gray-500 flex items-center gap-1"><Building2 className="h-3 w-3" />{selectedProject.ente}</span>}
              {selectedProject.anno_riferimento && <span className="text-xs text-gray-500 flex items-center gap-1"><Calendar className="h-3 w-3" />{selectedProject.anno_riferimento}</span>}
            </div>
            <h2 className="text-xl font-bold text-gray-900">{selectedProject.nome}</h2>
            {selectedProject.riferimento_normativo && <p className="text-sm text-gray-600 mt-1"><FileText className="h-3 w-3 inline mr-1" />{selectedProject.riferimento_normativo}</p>}
          </div>
          <Button onClick={() => downloadPdf(selectedProject)} className="bg-cyan-600 hover:bg-cyan-700" data-testid="download-pdf-detail"><Download className="h-4 w-4 mr-1" />PDF Autocertificazione</Button>
          <Button onClick={() => downloadGapExcel(selectedProject)} variant="outline" data-testid="download-gap-excel"><FileText className="h-4 w-4 mr-1" />Gap Analysis Excel</Button>
          {isSuperAdmin && (
            <Button onClick={() => { setImportFile(null); setImportResult(null); setShowImportDialog(true); }} variant="outline" data-testid="open-import-dialog">
              <Upload className="h-4 w-4 mr-1" />Importa requisiti
            </Button>
          )}
          {isSuperAdmin && selectedProject.codice === 'ADI_ABRUZZO_DGR272' && (
            <Button onClick={seedAbruzzo} variant="outline" data-testid="update-abruzzo-detail-btn" title="Aggiorna i 37 requisiti DGR 272 (descrizioni e checklist) all'ultima versione del manuale. Le risposte degli operatori vengono preservate.">
              <Sparkles className="h-4 w-4 mr-1" />Aggiorna manuale DGR 272
            </Button>
          )}
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="grid grid-cols-2 md:grid-cols-7 gap-3 mb-4">
            <StatBox color="cyan" label="% Conformità" value={`${stats.perc_conformita}%`} big />
            <StatBox color="indigo" label="% Completati" value={`${stats.perc_completamento}%`} big />
            <StatBox color="slate" label="Totale" value={stats.totale} />
            <StatBox color="emerald" label="Conformi" value={stats.conforme} />
            <StatBox color="amber" label="Parziali" value={stats.parziale} />
            <StatBox color="rose" label="Non Conformi" value={stats.non_conforme} />
            <StatBox color="gray" label="Da valutare" value={stats.non_valutati} />
          </div>
          {/* Conformity bar */}
          <div className="h-3 bg-gray-100 rounded-full overflow-hidden flex">
            <div className="bg-emerald-500" style={{ width: `${(stats.conforme / Math.max(stats.totale, 1)) * 100}%` }} title={`Conformi: ${stats.conforme}`} />
            <div className="bg-amber-500" style={{ width: `${(stats.parziale / Math.max(stats.totale, 1)) * 100}%` }} title={`Parziali: ${stats.parziale}`} />
            <div className="bg-rose-500" style={{ width: `${(stats.non_conforme / Math.max(stats.totale, 1)) * 100}%` }} title={`Non Conformi: ${stats.non_conforme}`} />
            <div className="bg-slate-400" style={{ width: `${(stats.non_applicabile / Math.max(stats.totale, 1)) * 100}%` }} title={`N.A.: ${stats.non_applicabile}`} />
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-3 flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
          <Input placeholder="Cerca per codice, titolo, descrizione..." value={search} onChange={e => setSearch(e.target.value)} className="pl-8" data-testid="acc-search" />
        </div>
        <Select value={filterStato} onValueChange={setFilterStato}>
          <SelectTrigger className="w-[180px]" data-testid="filter-stato"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti gli stati</SelectItem>
            <SelectItem value="da_valutare">Da valutare</SelectItem>
            {STATI.map(s => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterCategoria} onValueChange={setFilterCategoria}>
          <SelectTrigger className="w-[180px]" data-testid="filter-categoria"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutte le categorie</SelectItem>
            {Object.entries(CATEGORIE).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Requirements grouped */}
      <div className="space-y-3">
        {Object.keys(grouped).length === 0 && (
          <div className="bg-white rounded-lg border border-dashed border-gray-300 p-8 text-center text-gray-500">
            Nessun requisito trovato con i filtri attuali
          </div>
        )}
        {Object.entries(grouped).map(([cap, sezs]) => (
          <div key={cap} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="px-4 py-2 bg-gradient-to-r from-cyan-50 to-blue-50 border-b border-gray-200">
              <h3 className="font-bold text-cyan-900 text-sm uppercase tracking-wide">{cap}</h3>
            </div>
            {Object.entries(sezs).map(([sez, items]) => {
              const sectionKey = `${cap}/${sez}`;
              const isExpanded = expandedSections[sectionKey] !== false; // default expanded
              const sectionStats = items.reduce((acc, r) => {
                const st = r.response?.stato;
                if (!st) acc.daValutare++;
                else if (st === 'conforme') acc.conformi++;
                else if (st === 'parziale') acc.parziali++;
                else if (st === 'non_conforme') acc.nonConformi++;
                return acc;
              }, { conformi: 0, parziali: 0, nonConformi: 0, daValutare: 0 });
              return (
                <div key={sez} className="border-t border-gray-100">
                  <button
                    onClick={() => toggleSection(sectionKey)}
                    className="w-full px-4 py-2.5 flex items-center justify-between hover:bg-gray-50 transition"
                    data-testid={`section-toggle-${sez.substring(0, 20)}`}
                  >
                    <div className="flex items-center gap-2 text-left">
                      {isExpanded ? <ChevronDown className="h-4 w-4 text-gray-500" /> : <ChevronRightIcon className="h-4 w-4 text-gray-500" />}
                      <span className="font-semibold text-gray-800 text-sm">{sez}</span>
                      <span className="text-xs text-gray-500">({items.length})</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-xs">
                      {sectionStats.conformi > 0 && <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium">{sectionStats.conformi} conf</span>}
                      {sectionStats.parziali > 0 && <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">{sectionStats.parziali} parz</span>}
                      {sectionStats.nonConformi > 0 && <span className="px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 font-medium">{sectionStats.nonConformi} NC</span>}
                      {sectionStats.daValutare > 0 && <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-medium">{sectionStats.daValutare} da val.</span>}
                    </div>
                  </button>
                  {isExpanded && (
                    <div className="divide-y divide-gray-100">
                      {items.map(r => <RequirementRow key={r.id} req={r} onOpen={() => openRequirement(r)} />)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* Import Requirements Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent data-testid="import-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Upload className="h-5 w-5 text-cyan-600" />Importa requisiti da Excel/CSV</DialogTitle>
            <DialogDescription>Carica un file <code className="bg-gray-100 px-1 rounded">.xlsx</code> o <code className="bg-gray-100 px-1 rounded">.csv</code>. I requisiti con codice esistente vengono aggiornati (le risposte già inserite vengono preservate).</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-3 flex items-center gap-2">
              <Info className="h-5 w-5 text-cyan-700 shrink-0" />
              <div className="text-sm text-cyan-900 flex-1">
                Non hai un file pronto? Scarica il template con esempi e istruzioni.
              </div>
              <Button size="sm" variant="outline" onClick={downloadImportTemplate} data-testid="download-template-btn"><Download className="h-3.5 w-3.5 mr-1" />Template</Button>
            </div>
            <div>
              <Label>File da importare</Label>
              <Input
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={e => { setImportFile(e.target.files?.[0] || null); setImportResult(null); }}
                data-testid="import-file-input"
              />
              {importFile && <p className="text-xs text-gray-600 mt-1">Selezionato: <b>{importFile.name}</b> ({(importFile.size / 1024).toFixed(1)} KB)</p>}
            </div>
            {importResult && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm">
                <p className="font-semibold text-emerald-900 mb-1">Risultato import:</p>
                <ul className="text-emerald-900 space-y-0.5">
                  <li>• <b>{importResult.inserted}</b> requisiti nuovi creati</li>
                  <li>• <b>{importResult.updated}</b> requisiti esistenti aggiornati</li>
                  <li>• Righe totali processate: {importResult.total_rows}</li>
                </ul>
                {(importResult.errors || []).length > 0 && (
                  <div className="mt-2 pt-2 border-t border-emerald-200">
                    <p className="font-semibold text-amber-700">Errori ({importResult.errors.length}):</p>
                    <ul className="text-amber-800 text-xs space-y-0.5 mt-1 max-h-32 overflow-y-auto">
                      {importResult.errors.map((e, i) => <li key={i}>• {e}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImportDialog(false)}>Chiudi</Button>
            <Button onClick={importRequirementsFile} disabled={!importFile} className="bg-cyan-600 hover:bg-cyan-700" data-testid="import-confirm">Importa</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* My Assignments Dialog */}
      <Dialog open={showMyAssignments} onOpenChange={setShowMyAssignments}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="my-assignments-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Bell className="h-5 w-5 text-cyan-600" />Le mie verifiche di accreditamento</DialogTitle>
            <DialogDescription>{myAssignments.length} verifich{myAssignments.length === 1 ? 'a' : 'e'} assegnat{myAssignments.length === 1 ? 'a' : 'e'} al tuo nome</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {myAssignments.length === 0 && <p className="text-sm text-gray-500 text-center py-6 italic">Nessuna verifica assegnata</p>}
            {myAssignments.map(a => (
              <button
                key={`${a.project_id}-${a.requirement_id}`}
                onClick={() => {
                  const proj = projects.find(p => p.id === a.project_id);
                  if (proj) {
                    setSelectedProject(proj);
                    setShowMyAssignments(false);
                  }
                }}
                className={`w-full text-left p-3 rounded-lg border-2 transition hover:shadow-sm ${a.is_overdue ? 'border-rose-300 bg-rose-50' : (a.days_left !== null && a.days_left <= 7 ? 'border-amber-300 bg-amber-50' : 'border-gray-200 bg-white')}`}
                data-testid={`assignment-item-${a.requirement_codice}`}
              >
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-xs font-bold text-cyan-700">{a.requirement_codice}</span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{a.project_codice}</span>
                      {a.completato && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 flex items-center gap-0.5"><BadgeCheck className="h-3 w-3" />Verificato</span>}
                    </div>
                    <p className="text-sm font-medium text-gray-900 mt-0.5">{a.requirement_titolo}</p>
                    <p className="text-xs text-gray-500">{a.requirement_sezione}</p>
                  </div>
                  <div className="text-right shrink-0">
                    {a.scadenza_verifica ? (
                      <>
                        <p className={`text-sm font-bold ${a.is_overdue ? 'text-rose-700' : (a.days_left <= 3 ? 'text-amber-700' : 'text-gray-700')}`}>
                          {a.is_overdue ? `Scaduto da ${Math.abs(a.days_left)}gg` : `${a.days_left}gg rimasti`}
                        </p>
                        <p className="text-xs text-gray-500">{new Date(a.scadenza_verifica).toLocaleDateString('it-IT')}</p>
                      </>
                    ) : (
                      <p className="text-xs text-gray-400 italic">Nessuna scadenza</p>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Requirement edit dialog */}
      <Dialog open={showRequirementDialog} onOpenChange={setShowRequirementDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="requirement-dialog">
          {editingReq && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <span className="font-mono text-cyan-700 text-sm">{editingReq.codice}</span>
                  <span className="font-normal text-gray-400">·</span>
                  <span>{editingReq.titolo}</span>
                </DialogTitle>
                <DialogDescription>{editingReq.sezione}</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                {editingReq.descrizione && (
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Descrizione del requisito</p>
                    <p className="text-sm text-gray-800">{editingReq.descrizione}</p>
                  </div>
                )}
                {editingReq.evidenze_attese && (
                  <div className="bg-cyan-50 rounded-lg p-3 border border-cyan-200">
                    <p className="text-xs font-semibold text-cyan-700 uppercase mb-1">Evidenze attese</p>
                    <p className="text-sm text-cyan-900">{editingReq.evidenze_attese}</p>
                  </div>
                )}

                {/* Checklist di verifica puntuale (dal manuale) + items custom */}
                {((editingReq.checklist_items || []).length > 0 || (reqForm.checklist_custom_items || []).length > 0) && (
                  <ChecklistSection
                    seedItems={editingReq.checklist_items || []}
                    state={reqForm.checklist_state || {}}
                    customItems={reqForm.checklist_custom_items || []}
                    onSeedToggle={(idx, val) => setReqForm(f => ({ ...f, checklist_state: { ...(f.checklist_state || {}), [String(idx)]: val } }))}
                    onCustomToggle={(idx, val) => setReqForm(f => {
                      const list = [...(f.checklist_custom_items || [])];
                      list[idx] = { ...list[idx], completato: val };
                      return { ...f, checklist_custom_items: list };
                    })}
                    onCustomDelete={(idx) => setReqForm(f => ({ ...f, checklist_custom_items: (f.checklist_custom_items || []).filter((_, i) => i !== idx) }))}
                    onCustomAdd={(testo) => setReqForm(f => ({ ...f, checklist_custom_items: [...(f.checklist_custom_items || []), { testo, completato: false }] }))}
                  />
                )}
                {(editingReq.checklist_items || []).length === 0 && (reqForm.checklist_custom_items || []).length === 0 && (
                  <div className="border border-dashed border-gray-300 rounded-lg p-3 bg-gray-50">
                    <p className="text-xs font-semibold text-gray-700 uppercase mb-2">Checklist personalizzata</p>
                    <CustomItemAdder onAdd={(testo) => setReqForm(f => ({ ...f, checklist_custom_items: [...(f.checklist_custom_items || []), { testo, completato: false }] }))} />
                    <p className="text-[11px] text-gray-500 italic mt-2">Per questo requisito non ci sono punti predefiniti dal manuale. Puoi aggiungere voci personalizzate da spuntare durante la verifica.</p>
                  </div>
                )}

                <div>
                  <Label className="mb-2 block">Stato di conformità</Label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {STATI.map(s => {
                      const Icon = s.icon;
                      const active = reqForm.stato === s.key;
                      return (
                        <button
                          key={s.key}
                          type="button"
                          onClick={() => setReqForm(f => ({ ...f, stato: s.key }))}
                          className={`px-3 py-2 rounded-lg border text-sm font-medium transition flex items-center justify-center gap-1.5 ${active ? s.color + ' border-2' : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'}`}
                          data-testid={`stato-${s.key}`}
                        >
                          <Icon className="h-4 w-4" />{s.label}
                        </button>
                      );
                    })}
                  </div>
                  {(() => {
                    const seedMandatory = (editingReq.checklist_items || []).filter(i => i.obbligatorio);
                    const seedMandatoryDone = seedMandatory.filter((it, idx) => {
                      const realIdx = (editingReq.checklist_items || []).indexOf(it);
                      return reqForm.checklist_state?.[String(realIdx)] === true;
                    }).length;
                    const customMandatory = (reqForm.checklist_custom_items || []).filter(i => i.obbligatorio);
                    const customMandatoryDone = customMandatory.filter(i => i.completato).length;
                    const totalMandatory = seedMandatory.length + customMandatory.length;
                    const totalMandatoryDone = seedMandatoryDone + customMandatoryDone;
                    const allMandatoryDone = totalMandatory === 0 || totalMandatoryDone >= totalMandatory;
                    if (reqForm.stato === 'conforme' && !allMandatoryDone) {
                      return (
                        <div data-testid="conforme-block-warning" className="mt-2 flex items-start gap-2 px-3 py-2 rounded-lg bg-amber-50 border border-amber-300 text-amber-900 text-xs">
                          <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                          <span>
                            Lo stato <strong>Conforme</strong> richiede il completamento di tutti gli item obbligatori della checklist.
                            Mancanti: <strong>{totalMandatory - totalMandatoryDone}</strong> su {totalMandatory}.
                            Il salvataggio verra' bloccato dal sistema.
                          </span>
                        </div>
                      );
                    }
                    if (totalMandatory > 0) {
                      return (
                        <div className="mt-2 text-[11px] text-gray-500">
                          Item obbligatori checklist completati: <strong>{totalMandatoryDone}/{totalMandatory}</strong>
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>

                <div>
                  <Label>Note / Osservazioni</Label>
                  <Textarea rows={3} value={reqForm.note} onChange={e => setReqForm(f => ({ ...f, note: e.target.value }))} placeholder="Eventuali note sulla conformità, criticità rilevate, motivazioni..." data-testid="requirement-note" />
                </div>

                <div>
                  <Label>Evidenze documentali (testo libero)</Label>
                  <Textarea rows={2} value={reqForm.evidenze_text} onChange={e => setReqForm(f => ({ ...f, evidenze_text: e.target.value }))} placeholder="Es: Procedura PRO_001 v2.0, Verbale audit 12/03/2026, Registro tarature..." />
                </div>

                {procedures.length > 0 && (
                  <div>
                    <Label className="mb-2 block">Procedure interne collegate</Label>
                    <div className="border rounded-lg p-2 max-h-40 overflow-y-auto bg-gray-50 space-y-1">
                      {procedures.map(p => (
                        <label key={p.id} className="flex items-center gap-2 cursor-pointer hover:bg-white p-1 rounded">
                          <Checkbox
                            checked={reqForm.procedure_collegate_ids.includes(p.id)}
                            onCheckedChange={() => setReqForm(f => ({
                              ...f,
                              procedure_collegate_ids: f.procedure_collegate_ids.includes(p.id)
                                ? f.procedure_collegate_ids.filter(x => x !== p.id)
                                : [...f.procedure_collegate_ids, p.id],
                            }))}
                          />
                          <span className="text-sm"><span className="font-mono text-xs text-cyan-700">{p.codice}</span> {p.titolo}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                <label className="flex items-center gap-2 cursor-pointer p-3 rounded-lg bg-emerald-50 border border-emerald-200">
                  <Checkbox checked={reqForm.completato} onCheckedChange={v => setReqForm(f => ({ ...f, completato: !!v }))} data-testid="completato-check" />
                  <span className="text-sm font-medium text-emerald-900">Verifica completata e validata</span>
                </label>

                {/* Allegati / Evidenze documentali */}
                <div className="border border-gray-200 rounded-lg p-3 bg-white">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide flex items-center gap-1.5">
                      <Paperclip className="h-3.5 w-3.5" />
                      Allegati ({attachments.length})
                    </p>
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif"
                        onChange={uploadAttachment}
                        disabled={uploadingAttachment}
                        className="hidden"
                        data-testid="attachment-upload-input"
                      />
                      <span className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium transition ${uploadingAttachment ? 'bg-gray-100 text-gray-400 cursor-wait' : 'bg-cyan-600 text-white hover:bg-cyan-700 cursor-pointer'}`}>
                        {uploadingAttachment ? <><Loader2 className="h-3 w-3 animate-spin" />Caricamento...</> : <><Upload className="h-3 w-3" />Carica file</>}
                      </span>
                    </label>
                  </div>
                  <p className="text-[11px] text-gray-500 mb-2">
                    Formati ammessi: PDF, JPG, PNG, WEBP, HEIC · max 25 MB · <span className="text-cyan-700 font-medium">file compressi automaticamente</span>
                  </p>
                  {attachments.length === 0 ? (
                    <p className="text-xs text-gray-400 italic text-center py-3">Nessun allegato caricato</p>
                  ) : (
                    <div className="space-y-1.5">
                      {attachments.map(att => {
                        const isImage = (att.content_type || '').startsWith('image/');
                        const Icon = isImage ? ImageIcon : FileType;
                        const canDelete = isAdmin || att.uploaded_by === user.id;
                        return (
                          <div key={att.id} className="flex items-center gap-2 p-2 rounded border border-gray-100 hover:border-cyan-300 hover:bg-cyan-50/30 transition" data-testid={`attachment-item-${att.id}`}>
                            <div className={`shrink-0 h-8 w-8 rounded flex items-center justify-center ${isImage ? 'bg-amber-100 text-amber-700' : 'bg-rose-100 text-rose-700'}`}>
                              <Icon className="h-4 w-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate" title={att.original_filename}>{att.original_filename}</p>
                              <p className="text-[11px] text-gray-500">
                                {formatBytes(att.compressed_size)}
                                {att.saved_pct > 0 && <span className="text-cyan-700 font-medium"> · -{att.saved_pct}% (originale {formatBytes(att.original_size)})</span>}
                                {att.uploaded_by_name && <span> · {att.uploaded_by_name}</span>}
                              </p>
                            </div>
                            <button
                              onClick={() => downloadAttachment(att)}
                              className="shrink-0 p-1.5 text-gray-500 hover:text-cyan-600 hover:bg-cyan-50 rounded transition"
                              title="Scarica"
                              data-testid={`attachment-download-${att.id}`}
                            >
                              <Download className="h-4 w-4" />
                            </button>
                            {canDelete && (
                              <button
                                onClick={() => deleteAttachment(att)}
                                className="shrink-0 p-1.5 text-gray-400 hover:text-rose-600 hover:bg-rose-50 rounded transition"
                                title="Elimina"
                                data-testid={`attachment-delete-${att.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Assegnazione responsabile + scadenza */}
                {isAdmin && users.length > 0 && (
                  <div className="border border-indigo-200 bg-indigo-50/50 rounded-lg p-3 space-y-3">
                    <p className="text-xs font-semibold text-indigo-700 uppercase tracking-wide flex items-center gap-1"><UserCheck className="h-3.5 w-3.5" />Assegnazione e scadenza</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Responsabile verifica</Label>
                        <Select value={reqForm.responsabile_user_id || 'none'} onValueChange={v => setReqForm(f => ({ ...f, responsabile_user_id: v === 'none' ? '' : v }))}>
                          <SelectTrigger data-testid="responsabile-select"><SelectValue placeholder="Nessuno" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Nessuno</SelectItem>
                            {users.map(u => <SelectItem key={u.id} value={u.id}>{u.first_name} {u.last_name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs">Scadenza verifica</Label>
                        <Input type="date" value={reqForm.scadenza_verifica || ''} onChange={e => setReqForm(f => ({ ...f, scadenza_verifica: e.target.value }))} data-testid="scadenza-input" />
                      </div>
                    </div>
                  </div>
                )}

                {/* Audit collegato */}
                {(editingReq.response?.audit_id_collegato || editingReq.response?.audit_numero_collegato) && (
                  <div className="border border-cyan-200 bg-cyan-50 rounded-lg p-3 flex items-center gap-2">
                    <ClipboardCheck className="h-5 w-5 text-cyan-700" />
                    <div className="flex-1">
                      <p className="text-xs font-semibold text-cyan-700">Audit interno collegato</p>
                      <p className="text-sm font-mono text-cyan-900">{editingReq.response?.audit_numero_collegato || editingReq.response?.audit_id_collegato}</p>
                    </div>
                  </div>
                )}
              </div>
              <DialogFooter className="flex-wrap gap-2">
                {isAdmin && ['non_conforme', 'parziale'].includes(reqForm.stato) && !editingReq.response?.audit_id_collegato && (
                  <Button onClick={createAuditFromRequirement} variant="outline" className="border-cyan-500 text-cyan-700 hover:bg-cyan-50" data-testid="create-audit-from-req">
                    <ClipboardCheck className="h-4 w-4 mr-1" />Crea Audit interno
                  </Button>
                )}
                <Button variant="outline" onClick={() => setShowRequirementDialog(false)}>Annulla</Button>
                <Button onClick={saveRequirement} className="bg-cyan-600 hover:bg-cyan-700" data-testid="requirement-save">Salva</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ProjectCard({ project, onOpen, onDownload, onDelete, canDelete, authHeaders }) {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    let mounted = true;
    axios.get(`${API}/accreditamenti/projects/${project.id}/statistics`, { headers: authHeaders })
      .then(res => { if (mounted) setStats(res.data); })
      .catch(() => {});
    return () => { mounted = false; };
  }, [project.id, authHeaders]);

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition cursor-pointer" data-testid={`project-card-${project.codice}`} onClick={onOpen}>
      <div className="flex items-start justify-between mb-2">
        <div className="flex-1">
          <span className="px-2 py-0.5 rounded-full bg-cyan-100 text-cyan-700 text-[10px] font-mono font-bold uppercase tracking-wide">{project.codice}</span>
          <h3 className="font-bold text-gray-900 mt-1.5">{project.nome}</h3>
        </div>
        <Award className="h-5 w-5 text-cyan-500 shrink-0" />
      </div>
      {project.ente && <p className="text-xs text-gray-500 flex items-center gap-1 mb-1"><Building2 className="h-3 w-3" />{project.ente}</p>}
      {project.descrizione && <p className="text-xs text-gray-600 mt-2 line-clamp-2">{project.descrizione}</p>}

      {stats && (
        <div className="mt-3 pt-3 border-t border-gray-100 space-y-2">
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-600">Conformità</span>
            <span className="font-bold text-cyan-700 text-base">{stats.perc_conformita}%</span>
          </div>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden flex">
            <div className="bg-emerald-500" style={{ width: `${(stats.conforme / Math.max(stats.totale, 1)) * 100}%` }} />
            <div className="bg-amber-500" style={{ width: `${(stats.parziale / Math.max(stats.totale, 1)) * 100}%` }} />
            <div className="bg-rose-500" style={{ width: `${(stats.non_conforme / Math.max(stats.totale, 1)) * 100}%` }} />
            <div className="bg-slate-400" style={{ width: `${(stats.non_applicabile / Math.max(stats.totale, 1)) * 100}%` }} />
          </div>
          <div className="flex items-center justify-between text-[11px] text-gray-500">
            <span>{stats.totale} requisiti</span>
            <span>{stats.completati} verificati</span>
          </div>
        </div>
      )}

      <div className="mt-3 flex gap-1.5" onClick={e => e.stopPropagation()}>
        <Button size="sm" variant="outline" onClick={onOpen} className="flex-1" data-testid={`open-project-${project.codice}`}><Eye className="h-3.5 w-3.5 mr-1" />Apri</Button>
        <Button size="sm" variant="outline" onClick={onDownload} title="Download PDF"><Download className="h-3.5 w-3.5" /></Button>
        {canDelete && <Button size="sm" variant="outline" onClick={onDelete} className="text-rose-600 hover:text-rose-700"><Trash2 className="h-3.5 w-3.5" /></Button>}
      </div>
    </div>
  );
}

function RequirementRow({ req, onOpen }) {
  const stato = req.response?.stato;
  const statoData = findStato(stato);
  const StatoIcon = statoData?.icon || Circle;
  const cat = CATEGORIE[req.categoria];
  const completato = req.response?.completato;

  return (
    <button
      onClick={onOpen}
      className="w-full px-4 py-3 flex items-start gap-3 hover:bg-gray-50 transition text-left"
      data-testid={`req-row-${req.codice.replace(/\s|\./g, '')}`}
    >
      <div className={`mt-0.5 h-2 w-2 rounded-full ${statoData?.dot || 'bg-gray-300'} shrink-0`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-0.5">
          <span className="font-mono text-xs font-bold text-cyan-700">{req.codice}</span>
          {cat && <span className={`text-[10px] px-1.5 py-0.5 rounded ${cat.color} font-medium`}>{cat.label}</span>}
          {req.obbligatorio && <span className="text-[10px] px-1.5 py-0.5 rounded bg-rose-50 text-rose-600 font-medium">Obbligatorio</span>}
          {completato && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 font-medium flex items-center gap-0.5"><BadgeCheck className="h-3 w-3" />Verificato</span>}
        </div>
        <p className="text-sm font-medium text-gray-900 truncate">{req.titolo}</p>
        {req.response?.note && <p className="text-xs text-gray-500 italic mt-0.5 line-clamp-1">"{req.response.note}"</p>}
      </div>
      {statoData ? (
        <span className={`shrink-0 px-2 py-1 rounded-full text-[11px] font-semibold border ${statoData.color} flex items-center gap-1`}>
          <StatoIcon className="h-3 w-3" />{statoData.label}
        </span>
      ) : (
        <span className="shrink-0 px-2 py-1 rounded-full text-[11px] font-semibold bg-white border border-dashed border-gray-300 text-gray-400">
          Da valutare
        </span>
      )}
      <Edit className="h-4 w-4 text-gray-300 shrink-0 mt-1" />
    </button>
  );
}

function ChecklistSection({ seedItems, state, customItems, onSeedToggle, onCustomToggle, onCustomDelete, onCustomAdd }) {
  const totalSeed = seedItems.length;
  const checkedSeed = seedItems.filter((_, i) => state[String(i)] === true).length;
  const totalCustom = customItems.length;
  const checkedCustom = customItems.filter(c => c.completato).length;
  const total = totalSeed + totalCustom;
  const checked = checkedSeed + checkedCustom;
  const obbligatoriTotal = seedItems.filter(s => s.obbligatorio).length;
  const obbligatoriChecked = seedItems.filter((s, i) => s.obbligatorio && state[String(i)] === true).length;
  const allRequiredDone = obbligatoriTotal === 0 || obbligatoriChecked === obbligatoriTotal;

  return (
    <div className="border-2 border-amber-200 bg-amber-50/40 rounded-lg p-3">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide flex items-center gap-1.5">
            <ListChecks className="h-4 w-4" />
            Checklist di verifica
          </p>
          <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${allRequiredDone && checked === total ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
            {checked}/{total}
          </span>
        </div>
        {obbligatoriTotal > 0 && (
          <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${allRequiredDone ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
            Obbligatori: {obbligatoriChecked}/{obbligatoriTotal}
          </span>
        )}
      </div>

      <div className="space-y-1">
        {seedItems.map((item, idx) => {
          const isChecked = state[String(idx)] === true;
          return (
            <label key={idx} className={`flex items-start gap-2 p-2 rounded cursor-pointer transition ${isChecked ? 'bg-emerald-50/70' : 'hover:bg-white'}`} data-testid={`checklist-seed-${idx}`}>
              <Checkbox checked={isChecked} onCheckedChange={v => onSeedToggle(idx, !!v)} className="mt-0.5 shrink-0" />
              <div className="flex-1">
                <span className={`text-sm ${isChecked ? 'text-emerald-900 line-through opacity-75' : 'text-gray-800'}`}>{item.testo}</span>
                {item.obbligatorio === false && (
                  <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 font-medium">facoltativo</span>
                )}
              </div>
            </label>
          );
        })}
      </div>

      {customItems.length > 0 && (
        <div className="mt-2 pt-2 border-t border-amber-200/70">
          <p className="text-[11px] font-semibold text-amber-700 uppercase mb-1">Voci personalizzate</p>
          <div className="space-y-1">
            {customItems.map((item, idx) => {
              const isChecked = !!item.completato;
              return (
                <div key={idx} className={`flex items-start gap-2 p-2 rounded transition ${isChecked ? 'bg-emerald-50/70' : 'hover:bg-white'}`} data-testid={`checklist-custom-${idx}`}>
                  <Checkbox checked={isChecked} onCheckedChange={v => onCustomToggle(idx, !!v)} className="mt-0.5 shrink-0 cursor-pointer" />
                  <span className={`flex-1 text-sm ${isChecked ? 'text-emerald-900 line-through opacity-75' : 'text-gray-800'}`}>{item.testo}</span>
                  <button onClick={() => onCustomDelete(idx)} className="shrink-0 text-rose-400 hover:text-rose-600" title="Rimuovi">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="mt-3 pt-3 border-t border-amber-200/70">
        <CustomItemAdder onAdd={onCustomAdd} />
      </div>
    </div>
  );
}

function CustomItemAdder({ onAdd }) {
  const [text, setText] = useState('');
  const submit = () => {
    if (!text.trim()) return;
    onAdd(text.trim());
    setText('');
  };
  return (
    <div className="flex gap-2">
      <Input
        value={text}
        onChange={e => setText(e.target.value)}
        placeholder="Aggiungi voce personalizzata (es. ECG, ecografo, ecc.)"
        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); submit(); } }}
        className="flex-1 text-sm"
        data-testid="checklist-custom-input"
      />
      <Button size="sm" type="button" onClick={submit} variant="outline" className="border-amber-400 text-amber-700 hover:bg-amber-50" data-testid="checklist-custom-add">
        <Plus className="h-3.5 w-3.5 mr-1" />Aggiungi
      </Button>
    </div>
  );
}


function StatBox({ color, label, value, big }) {
  const colorMap = {
    cyan: 'bg-cyan-50 text-cyan-700 border-cyan-200',
    indigo: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    slate: 'bg-slate-50 text-slate-700 border-slate-200',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    rose: 'bg-rose-50 text-rose-700 border-rose-200',
    gray: 'bg-gray-50 text-gray-700 border-gray-200',
  };
  return (
    <div className={`rounded-lg border p-3 ${colorMap[color] || colorMap.slate}`}>
      <p className="text-[10px] font-semibold uppercase tracking-wide opacity-75">{label}</p>
      <p className={`font-bold ${big ? 'text-2xl' : 'text-xl'} mt-0.5`}>{value ?? 0}</p>
    </div>
  );
}
